import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Combobox } from '@/components/ui/combobox';

export interface SearchableProduct {
  id: string;
  name: string;
  sku?: string | null;
  serialTracking?: boolean;
  expiryTracking?: boolean;
}

interface Props<T extends SearchableProduct> {
  value: string;
  onChange: (id: string, product: T | null) => void;
  /** Extra /products filters, e.g. { trackStock: 'true' } or { serialTracking: 'true' } */
  filters?: Record<string, string>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  /** Product that must not be offered (e.g. the bundle itself) */
  excludeId?: string;
  /** Product already chosen (so its label shows before it appears in a search result) */
  selected?: T | null;
}

const PAGE = 30;

/**
 * POS-I257: product picker that searches the server (name, SKU, barcode) as the user types,
 * instead of loading one fixed page of products that silently drops the rest of a big catalogue.
 */
export function ProductSearchCombobox<T extends SearchableProduct>({
  value,
  onChange,
  filters,
  placeholder = 'Select a product…',
  searchPlaceholder = 'Search by name, SKU or barcode...',
  emptyText,
  disabled,
  excludeId,
  selected,
}: Props<T>) {
  const { t } = useTranslation('common');
  const [typed, setTyped] = useState('');
  const [debounced, setDebounced] = useState('');
  const [chosen, setChosen] = useState<T | null>(selected ?? null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(typed.trim()), 300);
    return () => clearTimeout(timer);
  }, [typed]);

  const filterKey = JSON.stringify(filters ?? {});
  const { data, isFetching } = useQuery({
    queryKey: ['product-search-combobox', filterKey, debounced],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(PAGE), page: '1', sortBy: 'name', sortOrder: 'asc', ...(filters ?? {}) });
      if (debounced) params.set('search', debounced);
      const res = await api.get<{ data?: T[] }>(`/products?${params}`);
      return res.data?.data ?? [];
    },
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });

  const rows: T[] = (data ?? []).filter((p) => p.id !== excludeId);
  // POS-I258: keep the chosen product in the list only while it still matches what is typed,
  // so a search with no match shows the empty message instead of the previous choice.
  const term = typed.trim().toLowerCase();
  const chosenMatches =
    !!chosen &&
    chosen.id !== excludeId &&
    (!term || chosen.name.toLowerCase().includes(term) || (chosen.sku ?? '').toLowerCase().includes(term));
  const merged = chosen && chosenMatches && !rows.some((p) => p.id === chosen.id) ? [chosen, ...rows] : rows;

  return (
    <Combobox
      serverSearch
      loading={isFetching || typed.trim() !== debounced}
      options={merged.map((p) => ({ value: p.id, label: p.name, sublabel: p.sku ? `SKU: ${p.sku}` : undefined }))}
      value={value}
      onChange={(id) => {
        const product = merged.find((p) => p.id === id) ?? null;
        setChosen(product);
        onChange(id, product);
      }}
      onFilterChange={setTyped}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText={emptyText ?? t('productPickerEmpty')}
      disabled={disabled}
    />
  );
}
